/**
 * Share Details page component module.
 * @module investor/shared/pages/ShareDetails
 */

import React, {useEffect, useState} from 'react';
import history from '../../../history';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {useParams} from 'react-router-dom';
import useSWR from 'swr';
import moment from 'moment';

import {Button, Column, DescriptiveText, Page, Row, Table, Tile} from '../../../shared';
import debug from 'debug';
import {_openModal} from '../../../shared/reducers/modals';
import {gatherData, getUpdatedFilter, setSectionURI} from '../../../shared/utils/utils';

const log = debug('circleblack:share-details');
/**
 * Share Details page component to view document share details.
 * @name ShareDetails
 */
const ShareDetails = props => {
  const {openModal, userProfile} = props;
  const {id} = useParams();
  const getSharedAdvisors = gatherData(`/documents/${id}/advisors`);
  const getDocument = gatherData(`/documents/${id}`);
  const defaultParams = {};
  const [document, setDocument] = useState({});
  const [bodyCopy, setBodyCopy] = useState('');
  const [sharedAdvisors, setSharedAdvisors] = useState([]);
  const [selectedAdvisors, setSelectedAdvisors] = useState([]);
  const {data: advisors} = useSWR('/user/advisors');

  const shareDataManager = {
    gatherData: inputValue => getSharedAdvisors({
      ...getUpdatedFilter(defaultParams),
      ...inputValue,
    })
      .then(sAdvisors => {
        setSharedAdvisors(sAdvisors);
        return sAdvisors;
      })
      .catch(err => {
        log('err thrown');
        openModal({
          type: 'Error',
          title: 'Failed to Retrieve Share Details',
          err: err,
        });
        history.push(setSectionURI());
        throw err;
      }),
    setSelectedRows: (rd) => {
      setSelectedAdvisors(rd);
    },
  };

  const genBodyCopy = () => {
    let bodyText = bodyCopy;
    if (document.shareApproval !== 'DENIED') {
      if (document.assocHousehold) {
        bodyText = `${document.title} is intended for ${document.assocHousehold} and can only be shared with them.`;
      } else {
        bodyText = (<p>You can click the “Share” button to send {document.title} to your {userProfile.lowerCasePluralAdvisorTerm}.
          Below shows you who it has already been shared with and who has viewed it.
          You can also select to “Unshare” it from select {userProfile.lowerCasePluralAdvisorTerm} or from all.</p>);
      }
    }
    return setBodyCopy(bodyText);
  };

  const disableAdvisorShared = (rowData) => rowData.id === userProfile.id || !!rowData.assocHousehold;

  const sharedTable = (
    <Table dataManager={shareDataManager}
           columns={[
             {
               title: 'Advisor',
               dataPoint: 'name',
               type: 'text',
               minWidth: 500,
               alignment: 'left',
               isSortable: true,
             },
             {
               title: 'Email',
               dataPoint: 'email',
               alignment: 'left',
               type: 'text',
               minWidth: 300,
               isSortable: true,
             },
             {
               title: 'Read',
               dataPoint: 'readAt',
               alignment: 'left',
               type: 'date',
               render: row => row.readAt ? moment(row.readAt, 'YYYY/MM/DD HH:mm:ss').format('M/D/YY h:mma') : 'No',
               minWidth: 100,
               isSortable: true,
             },
           ]}
           select={{type: 'all', max: 100, rowDisabledFunc: disableAdvisorShared}}
           defaultSort={{column: 'name', desc: false}}
           rowIdentifier={'id'}
           height={'750px'}/>
  );

  useEffect(() => {
    if (!document.id) {
      getDocument()
        .then(doc => {
          if (doc.notShareable || doc.cat === 'Report' && !doc.assocHouseholdID) {
            history.push(setSectionURI());
          }
          genBodyCopy();
          setDocument(doc);
        })
        .catch(function error(err) {
          log('error was caught in useEffect', err);
          const res = err.response;
          if (!(res.status < 500 && res.data.error && res.data.error.msg)) {
            err.message = 'Unfortunately we encountered an error retrieving the document. If you have any questions, please contact CircleBlack Client Services at help@circleblack.com or 800-315-1241.';
          }
          openModal({
            type: 'Error',
            title: 'Failed to Retrieve Share related info',
            err: err,
          });
        });
    }
  }, []);

  log('rendering ShareDetails');
  return <Page>
    <Tile key='ShareDetails' title={'Share Details'}>
      <Button importance={'secondary'} size={'md'} label={'Back'} action={() => history.push(setSectionURI())} width={'240px'}/>
      <DescriptiveText>
        {bodyCopy}
      </DescriptiveText>
      <Row wrap={false}>
        <Column width={'400px'}>
          <Button importance={'secondary'}
                  size={'md'}
                  label={'Share'}
                  action={() => {history.push(setSectionURI(`/${id}/share`));}}
                  disabled={advisors?.length === sharedAdvisors.length}
                  width={'300px'}/>
        </Column>
        <Column width={'400px'}>
          <Button importance={'secondary'}
                  size={'md'}
                  label={'Unshare'}
                  action={() => openModal({
                    componentName: 'UnshareDocument',
                    selected: selectedAdvisors,
                    document: document,
                    refresh: () => {
                      shareDataManager.toggleAllPageRowsSelected(false);
                      shareDataManager.refresh();
                    },
                  })}
                  disabled={selectedAdvisors && !selectedAdvisors.length}
                  width={'300px'}/>
        </Column>
      </Row>
      {document.id && sharedTable}
    </Tile>
  </Page>;
};

const mapDispatchToProps = dispatch => ({
  openModal: modalToOpen => dispatch(_openModal(modalToOpen)),
});

const mapStateToProps = state => ({
  userProfile: state.session.profile,
});

export default connect(mapStateToProps, mapDispatchToProps)(ShareDetails);

ShareDetails.propTypes = {
  /**
   * The open function that dispatches _openModal
   */
  openModal: PropTypes.func.isRequired,
  userProfile: PropTypes.object.isRequired,
  /**
   * The state passed in, in this case if compliance.
   */
  history: PropTypes.func,
};
